import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import {
	Line,
	LineChart,
	XAxis,
	YAxis,
	ResponsiveContainer,
	Tooltip,
	CartesianGrid,
	Label,
} from "recharts";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { formatDate } from "@/lib/utils/date";
import {
	AlertTriangle,
	FileDown,
	BarChart,
	FileText,
	FileType,
	FileSpreadsheet,
	FileCode,
	Loader2,
	Brain,
	Sparkles,
	Zap,
	Target,
	Image,
	Smartphone,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { ChartExportButton } from "@/components/ui/chart-export-button";
import { ExportAllChartsButton } from "@/components/ui/export-all-charts-button";
import {
	useGetDevicesQuery,
	useGetLocationsQuery,
	useGetAnalyticsSensorAggregatesQuery,
	useGetAnalyticsSensorDataQuery,
	useGetAnalyticsDeviceStatusQuery,
	type GetAnalyticsSensorAggregatesApiArg,
} from "@/services";

// Extended interval type that includes 'realtime' for raw data points
type AnalyticsInterval = Required<GetAnalyticsSensorAggregatesApiArg>["interval"] | "realtime";
import { useAppSelector } from "@/ducks/useful-hooks";
import { format, subDays } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GaugeChart } from "@/components/analytics/GaugeChart";
import { DeviceStatusTable } from "@/components/analytics/DeviceStatusTable";
import { toast } from "@/components/ui/toaster";
import { aiService } from "@/services/ai-service";
import {
	AIPredictionWidget,
	AIMetricsWidget,
} from "@/components/ai/ai-dashboard-widgets";
import { Badge } from "@/components/ui/badge";
import { AnalyticsFilters } from "@/components/analytics/filters/analytics-filters";
import { StatusCards } from "@/components/analytics/status-cards";
import { DashboardHeader } from "@/components/analytics/dashboard-header";
import { 
	useAnalyticsPreferencesStore,
	SYSTEM_DEFAULTS,
	type FilterPreset,
	type FilterValues,
} from "@/stores/analytics-preferences-store";
import { apiConfig } from "@/config";

// Theme-compliant color palette
const THEME_COLORS = {
	chart1: "hsl(var(--chart-1))", // Primary teal
	chart2: "hsl(var(--chart-2))", // Blue
	chart3: "hsl(var(--chart-3))", // Indigo
	chart6: "hsl(var(--chart-6))", // Red/Orange
	chart7: "hsl(var(--chart-7))", // Amber
	chart8: "hsl(var(--chart-8))", // Green
	success: "hsl(var(--success))",
	warning: "hsl(var(--warning))",
	destructive: "hsl(var(--destructive))",
};

// Types
interface Location {
	id: string;
	name: string;
	subLocations?: Location[];
}

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, unit, timezone = 'local' }: any) => {
	if (active && payload && payload.length) {
		const formattedDate = timezone === 'local' 
			? formatDate.fullDateTime(label)
			: new Intl.DateTimeFormat('en-US', {
				timeZone: timezone,
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
				hour12: true,
			}).format(new Date(label));

		return (
			<div className="bg-background/95 border rounded-lg shadow-lg p-3 backdrop-blur-sm">
				<p className="text-sm font-medium">{formattedDate}</p>
				{payload.map((entry: any, index: number) => (
					<div key={index} className="flex items-center gap-2 mt-1">
						<div
							className="w-3 h-3 rounded-full"
							style={{ backgroundColor: entry.color }}
						/>
						<span className="text-sm">
							{entry.name}: {entry.value?.toFixed(1) ?? 'N/A'} {unit || entry.unit || ""}
						</span>
					</div>
				))}
			</div>
		);
	}
	return null;
};

// Format date based on interval
const formatDateByInterval = (timestamp: number, interval: string): string => {
	const date = new Date(timestamp);
	switch (interval) {
		case "realtime":
			return format(date, "HH:mm:ss");
		case "minute":
			return format(date, "HH:mm");
		case "hour":
			return format(date, "ha");
		case "day":
			return format(date, "d MMM");
		case "week":
			return format(date, "d MMM");
		case "month":
			return format(date, "MMM");
		default:
			return format(date, "HH:mm");
	}
};

// Downsample data using the Largest-Triangle-Three-Buckets (LTTB) algorithm.
// This preserves the visual shape of the data while reducing the number of points
// to avoid SVG rendering issues with very dense datasets.
const downsampleData = (
	data: any[],
	targetPoints: number,
	dataKey: string,
): any[] => {
	if (!data || data.length <= targetPoints) return data;

	const result: any[] = [];
	// Always keep the first point
	result.push(data[0]);

	const bucketSize = (data.length - 2) / (targetPoints - 2);

	let prevIndex = 0;

	for (let i = 1; i < targetPoints - 1; i++) {
		const bucketStart = Math.floor((i - 1) * bucketSize) + 1;
		const bucketEnd = Math.min(Math.floor(i * bucketSize) + 1, data.length - 1);

		// Calculate the average point of the next bucket for the triangle area calculation
		const nextBucketStart = Math.floor(i * bucketSize) + 1;
		const nextBucketEnd = Math.min(Math.floor((i + 1) * bucketSize) + 1, data.length - 1);

		let avgX = 0;
		let avgY = 0;
		let nextBucketCount = 0;
		for (let j = nextBucketStart; j <= nextBucketEnd && j < data.length; j++) {
			avgX += data[j].timestamp;
			avgY += (data[j][dataKey] ?? 0);
			nextBucketCount++;
		}
		if (nextBucketCount > 0) {
			avgX /= nextBucketCount;
			avgY /= nextBucketCount;
		}

		// Find the point in the current bucket that forms the largest triangle
		let maxArea = -1;
		let bestIndex = bucketStart;

		const prevX = data[prevIndex].timestamp;
		const prevY = data[prevIndex][dataKey] ?? 0;

		for (let j = bucketStart; j <= bucketEnd && j < data.length; j++) {
			const curX = data[j].timestamp;
			const curY = data[j][dataKey] ?? 0;

			// Triangle area calculation
			const area = Math.abs(
				(prevX - avgX) * (curY - prevY) -
				(prevX - curX) * (avgY - prevY)
			);

			if (area > maxArea) {
				maxArea = area;
				bestIndex = j;
			}
		}

		result.push(data[bestIndex]);
		prevIndex = bestIndex;
	}

	// Always keep the last point
	result.push(data[data.length - 1]);

	return result;
};

// Format date in specified timezone
const formatDateInTimezone = (date: Date, timezone: string, formatStr: string): string => {
	if (timezone === 'local') {
		return format(date, formatStr);
	}
	try {
		return new Intl.DateTimeFormat('en-US', {
			timeZone: timezone,
			year: formatStr.includes('y') || formatStr.includes('P') ? 'numeric' : undefined,
			month: formatStr.includes('M') || formatStr.includes('P') ? 'short' : undefined,
			day: formatStr.includes('d') || formatStr.includes('P') ? 'numeric' : undefined,
			hour: formatStr.includes('H') || formatStr.includes('h') ? 'numeric' : undefined,
			minute: formatStr.includes('m') ? '2-digit' : undefined,
			hour12: formatStr.includes('a') || formatStr.includes('h'),
		}).format(date);
	} catch {
		return format(date, formatStr);
	}
};

// Format date based on interval with timezone support
const formatDateByIntervalWithTimezone = (timestamp: number, interval: string, timezone: string): string => {
	const date = new Date(timestamp);
	if (timezone === 'local') {
		return formatDateByInterval(timestamp, interval);
	}
	try {
		const options: Intl.DateTimeFormatOptions = { timeZone: timezone };
		switch (interval) {
			case "realtime":
				options.hour = '2-digit';
				options.minute = '2-digit';
				options.second = '2-digit';
				break;
			case "minute":
				options.hour = '2-digit';
				options.minute = '2-digit';
				break;
			case "hour":
				options.hour = 'numeric';
				options.hour12 = true;
				break;
			case "day":
			case "week":
				options.day = 'numeric';
				options.month = 'short';
				break;
			case "month":
				options.month = 'short';
				break;
			default:
				options.hour = '2-digit';
				options.minute = '2-digit';
		}
		return new Intl.DateTimeFormat('en-US', options).format(date);
	} catch {
		return formatDateByInterval(timestamp, interval);
	}
};

// Visualization component for aggregated sensor data
function SensorAggregateChart({
	data,
	title,
	color,
	unit,
	dataKey = "avg",
	secondaryDataKey,
	secondaryColor,
	domainMin,
	domainMax,
	interval,
	id,
	deviceInfo,
	locationInfo,
	timezone = "local",
}: {
	data: any[];
	title: string;
	color: string;
	unit: string;
	dataKey?: string;
	secondaryDataKey?: string;
	secondaryColor?: string;
	domainMin?: number;
	domainMax?: number;
	interval: string;
	id: string;
	deviceInfo?: string;
	locationInfo?: string;
	timezone?: string;
}) {
	if (!data || data.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
					<CardDescription>No data available</CardDescription>
				</CardHeader>
				<CardContent className="h-[280px] sm:h-[330px] flex items-center justify-center text-muted-foreground">
					No sensor data found for the selected period
				</CardContent>
			</Card>
		);
	}

	const rawFormattedData = data.map((item) => ({
		...item,
		timestamp: new Date(item.timestamp).getTime(),
	}));

	// For dense datasets (minute/realtime intervals), downsample to avoid
	// SVG rendering issues while preserving the visual shape of the data.
	// The LTTB algorithm keeps the most visually significant points.
	const MAX_CHART_POINTS = 1000;
	const formattedData = rawFormattedData.length > MAX_CHART_POINTS
		? downsampleData(rawFormattedData, MAX_CHART_POINTS, dataKey)
		: rawFormattedData;

	// Use linear interpolation for dense data to avoid monotone spline
	// overshooting issues; use monotone for sparser data for smoother curves
	const curveType = rawFormattedData.length > 500 ? "linear" : "monotone" as const;

	// Calculate min/max for Y axis domain (excluding null values)
	// Use reduce instead of Math.min/max(...spread) to avoid call stack overflow with large arrays
	const validValues = rawFormattedData.map((item) => item[dataKey]).filter((v) => v != null);
	const dataMin = validValues.reduce((min, v) => (v < min ? v : min), Infinity);
	const dataMax = validValues.reduce((max, v) => (v > max ? v : max), -Infinity);
	// Use additive margin based on range to correctly handle negative values and zero ranges
	const range = dataMax - dataMin;
	const margin = range === 0 ? Math.max(Math.abs(dataMin) * 0.1, 1) : range * 0.1;
	const minValue = domainMin !== undefined ? domainMin : Math.floor(dataMin - margin);
	const maxValue = domainMax !== undefined ? domainMax : Math.ceil(dataMax + margin);

	// Calculate current value (most recent data point with valid data)
	const latestValue = rawFormattedData[rawFormattedData.length - 1]?.[dataKey] || 0;

	// Get date range for metadata (with timezone)
	const startDate =
		rawFormattedData.length > 0
			? formatDateInTimezone(new Date(rawFormattedData[0]?.timestamp), timezone, "PPP")
			: "";
	const endDate =
		rawFormattedData.length > 0
			? formatDateInTimezone(
					new Date(rawFormattedData[rawFormattedData.length - 1]?.timestamp),
					timezone,
					"PPP",
				)
			: "";
	const dateRangeStr = `${startDate} - ${endDate}`;

	// Get timezone display label
	const timezoneLabel = timezone === 'local' ? 'Local Time' : timezone.replace(/_/g, ' ').split('/').pop();

	// Calculate useful statistics (using raw data without gap markers)
	const avgValue =
		rawFormattedData.reduce((sum, item) => sum + item[dataKey], 0) /
		rawFormattedData.length;
	const stdDev = Math.sqrt(
		rawFormattedData.reduce(
			(sum, item) => sum + Math.pow(item[dataKey] - avgValue, 2),
			0,
		) / rawFormattedData.length,
	);
	const medianValue = [...validValues].sort((a, b) => a - b)[
		Math.floor(validValues.length / 2)
	];

	return (
		<Card>
			<CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 gap-2">
				<div className="flex-1 min-w-0">
					<CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
						<span className="truncate">{title}</span>
						<span className="text-base sm:text-lg font-semibold whitespace-nowrap">
							{latestValue.toFixed(1)} {unit}
						</span>
					</CardTitle>
					<CardDescription data-date-range={dateRangeStr} className="text-xs">
						{startDate} - {endDate}
						<span className="ml-2 text-muted-foreground/70">({timezoneLabel})</span>
					</CardDescription>
				</div>
				<ChartExportButton
					chartId={id}
					filename={`${title.toLowerCase().replace(/\s+/g, "-")}`}
					metadata={{
						title: title,
						description: `${title} data over time (${interval} intervals)`,
						dateRange: dateRangeStr,
						additionalInfo: {
							"Sensor Type": title,
							Unit: unit,
							Device: deviceInfo || "All Devices",
							Location: locationInfo || "All Locations",
							"Time Interval": interval,
							"Timezone": timezoneLabel,
							Average: `${avgValue.toFixed(2)} ${unit}`,
							Median: `${medianValue.toFixed(2)} ${unit}`,
							"Standard Deviation": `${stdDev.toFixed(2)}`,
							Min: `${minValue.toFixed(2)} ${unit}`,
							Max: `${maxValue.toFixed(2)} ${unit}`,
							Latest: `${latestValue.toFixed(2)} ${unit}`,
							"Data Points": `${rawFormattedData.length}`,
							"Start Date": startDate,
							"End Date": endDate,
						},
					}}
				/>
			</CardHeader>
		<CardContent className="px-2 sm:px-6">
			<div className="h-[280px] sm:h-[330px]" id={id}>
				<ResponsiveContainer width="100%" height="100%">
					<LineChart
						data={formattedData}
						margin={{ top: 10, right: 10, left: 20, bottom: 45 }}
					>
						<CartesianGrid
							strokeDasharray="3 3"
							vertical={false}
							opacity={0.2}
						/>
						<XAxis
							dataKey="timestamp"
							tickFormatter={(time) => formatDateByIntervalWithTimezone(time, interval, timezone)}
							stroke="hsl(var(--muted-foreground))"
							fontSize={10}
							type="number"
							domain={["auto", "auto"]}
							scale="time"
							tick={{ fontSize: 10 }}
						>
							<Label
								value="Time"
								position="bottom"
								offset={20}
								style={{
									textAnchor: 'middle',
									fill: 'hsl(var(--muted-foreground))',
									fontSize: 12,
								}}
							/>
						</XAxis>
						<YAxis
							domain={[minValue, maxValue]}
							stroke="hsl(var(--muted-foreground))"
							fontSize={10}
							tickFormatter={(value) => `${value.toFixed(1)}`}
							width={55}
							tick={{ fontSize: 10 }}
						>
							<Label
								value={unit}
								position="left"
								angle={-90}
								offset={0}
								style={{
									textAnchor: 'middle',
									fill: 'hsl(var(--muted-foreground))',
									fontSize: 12,
								}}
							/>
						</YAxis>
							<Tooltip
								content={<CustomTooltip unit={unit} timezone={timezone} />}
								labelFormatter={(label) =>
									formatDate.fullDateTime(new Date(label))
								}
							/>
							<Line
								type={curveType}
								dataKey={dataKey}
								name={title}
								stroke={color}
								strokeWidth={2}
								dot={false}
								activeDot={{ r: 6, strokeWidth: 0 }}
								isAnimationActive={formattedData.length <= 300}
								animationDuration={300}
								connectNulls={true}
							/>
							{secondaryDataKey && secondaryColor && (
								<Line
									type={curveType}
									dataKey={secondaryDataKey}
									name={`${title} (${secondaryDataKey})`}
									stroke={secondaryColor}
									strokeWidth={1.5}
									strokeDasharray="5 5"
									dot={false}
									isAnimationActive={formattedData.length <= 300}
									animationDuration={300}
									connectNulls={true}
								/>
							)}
						</LineChart>
					</ResponsiveContainer>
				</div>
				<div className="mt-3 text-xs text-muted-foreground flex flex-wrap justify-between gap-2">
					<span className="whitespace-nowrap">
						Min: {minValue.toFixed(1)} {unit}
					</span>
					<span className="whitespace-nowrap">
						Avg: {avgValue.toFixed(1)} {unit}
					</span>
					<span className="whitespace-nowrap">
						Max: {maxValue.toFixed(1)} {unit}
					</span>
				</div>
			</CardContent>
		</Card>
	);
}

export const Route = createFileRoute(
	"/_authenticated/(user-dashboard)/dashboard/_dashboard/analytics",
)({
	component: RouteComponent,
});

function RouteComponent() {
	// Get auth state for API calls
	const { accessToken, currentTenant } = useAppSelector((state) => state.auth);
	
	// Get preferences store
	const { activePresetId, setActivePreset, getActivePreset } = useAnalyticsPreferencesStore();
	
	const [liveData, setLiveData] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [preferencesInitialized, setPreferencesInitialized] = useState(false);
	
	// Get the active preset for initialization
	const activePreset = getActivePreset();
	
	// Applied filter state (used for API calls) - initialized from active preset or system defaults
	const [appliedFilters, setAppliedFilters] = useState(() => ({
		location: activePreset?.locationId ?? SYSTEM_DEFAULTS.locationId,
		device: activePreset?.deviceId ?? SYSTEM_DEFAULTS.deviceId,
		dateRange: {
			from: subDays(new Date(), activePreset?.dateRangeDays ?? SYSTEM_DEFAULTS.dateRangeDays),
			to: new Date(),
		},
		interval: (activePreset?.interval ?? SYSTEM_DEFAULTS.interval) as AnalyticsInterval,
		timezone: activePreset?.timezone ?? SYSTEM_DEFAULTS.timezone,
	}));

	// Pending filter state (user is editing but hasn't applied yet)
	const [pendingLocation, setPendingLocation] = useState<string>(activePreset?.locationId ?? SYSTEM_DEFAULTS.locationId);
	const [pendingDevice, setPendingDevice] = useState<string>(activePreset?.deviceId ?? SYSTEM_DEFAULTS.deviceId);
	const [pendingDateRange, setPendingDateRange] = useState<{ from: Date; to: Date }>({
		from: subDays(new Date(), activePreset?.dateRangeDays ?? SYSTEM_DEFAULTS.dateRangeDays),
		to: new Date(),
	});
	const [pendingInterval, setPendingInterval] =
		useState<AnalyticsInterval>(
			(activePreset?.interval ?? SYSTEM_DEFAULTS.interval) as AnalyticsInterval
		);
	const [pendingTimezone, setPendingTimezone] = useState<string>(activePreset?.timezone ?? SYSTEM_DEFAULTS.timezone);

	const [activeTab, setActiveTab] = useState<string>(activePreset?.tab ?? SYSTEM_DEFAULTS.tab ?? 'timeseries');
	const [aiInsights, setAiInsights] = useState<any[]>([]);
	const [aiLoading, setAiLoading] = useState(false);

	// Initialize from active preset on mount (handles hydration from localStorage)
	useEffect(() => {
		if (!preferencesInitialized && activePresetId) {
			const preset = getActivePreset();
			if (preset) {
				// Update applied filters from stored preset
				setAppliedFilters({
					location: preset.locationId,
					device: preset.deviceId,
					dateRange: {
						from: subDays(new Date(), preset.dateRangeDays),
						to: new Date(),
					},
					interval: preset.interval,
					timezone: preset.timezone,
				});
				
				// Update pending filters to match
				setPendingLocation(preset.locationId);
				setPendingDevice(preset.deviceId);
				setPendingDateRange({
					from: subDays(new Date(), preset.dateRangeDays),
					to: new Date(),
				});
				setPendingInterval(preset.interval);
				setPendingTimezone(preset.timezone);
				setActiveTab(preset.tab);
			}
			setPreferencesInitialized(true);
		}
	}, [activePresetId, preferencesInitialized, getActivePreset]);

	// Check if there are unapplied filter changes
	const hasUnappliedChanges = useMemo(() => {
		return (
			pendingLocation !== appliedFilters.location ||
			pendingDevice !== appliedFilters.device ||
			pendingDateRange.from.getTime() !== appliedFilters.dateRange.from.getTime() ||
			pendingDateRange.to.getTime() !== appliedFilters.dateRange.to.getTime() ||
			pendingInterval !== appliedFilters.interval ||
			pendingTimezone !== appliedFilters.timezone
		);
	}, [pendingLocation, pendingDevice, pendingDateRange, pendingInterval, pendingTimezone, appliedFilters]);

	// Apply filters handler
	const handleApplyFilters = () => {
		setAppliedFilters({
			location: pendingLocation,
			device: pendingDevice,
			dateRange: pendingDateRange,
			interval: pendingInterval,
			timezone: pendingTimezone,
		});
	};

	// Calculate date range in days from a date range
	const calculateDateRangeDays = (from: Date, to: Date): number => {
		const diffTime = Math.abs(to.getTime() - from.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	// Load a preset and apply its filters
	const handleLoadPreset = (preset: FilterPreset) => {
		const newDateRange = {
			from: subDays(new Date(), preset.dateRangeDays),
			to: new Date(),
		};
		
		// Update applied filters
		setAppliedFilters({
			location: preset.locationId,
			device: preset.deviceId,
			dateRange: newDateRange,
			interval: preset.interval,
			timezone: preset.timezone,
		});
		
		// Update pending filters to match
		setPendingLocation(preset.locationId);
		setPendingDevice(preset.deviceId);
		setPendingDateRange(newDateRange);
		setPendingInterval(preset.interval);
		setPendingTimezone(preset.timezone);
		setActiveTab(preset.tab);
		
		toast.success("Preset loaded", {
			title: "Success",
			description: `Loaded preset "${preset.name}"`,
			duration: 2000,
		});
	};

	// Reset to system defaults
	const handleResetToDefaults = () => {
		setActivePreset(null);
		
		// Reset all filter states to system defaults
		const defaultDateRange = {
			from: subDays(new Date(), SYSTEM_DEFAULTS.dateRangeDays),
			to: new Date(),
		};
		
		setAppliedFilters({
			location: SYSTEM_DEFAULTS.locationId,
			device: SYSTEM_DEFAULTS.deviceId,
			dateRange: defaultDateRange,
			interval: SYSTEM_DEFAULTS.interval,
			timezone: SYSTEM_DEFAULTS.timezone,
		});
		
		setPendingLocation(SYSTEM_DEFAULTS.locationId);
		setPendingDevice(SYSTEM_DEFAULTS.deviceId);
		setPendingDateRange(defaultDateRange);
		setPendingInterval(SYSTEM_DEFAULTS.interval);
		setPendingTimezone(SYSTEM_DEFAULTS.timezone);
		setActiveTab(SYSTEM_DEFAULTS.tab ?? 'timeseries');
		
		toast.success("Filters reset", {
			title: "Success",
			description: "Filters have been reset to system defaults.",
			duration: 2000,
		});
	};

	// Add queries for locations and devices
	const { data: locationsData } = useGetLocationsQuery({});

	const { data: devicesData } = useGetDevicesQuery({
		locationId: appliedFilters.location || undefined,
	});

	// Determine if we're in realtime mode (raw data) or aggregation mode
	const isRealtimeMode = appliedFilters.interval === "realtime";

	// Fetch aggregated sensor data using APPLIED filters (skip when in realtime mode)
	const {
		data: sensorAggregates,
		isLoading: isLoadingAggregates,
		error: aggregatesError,
		refetch: refetchAggregates,
	} = useGetAnalyticsSensorAggregatesQuery(
		{
			locationId: appliedFilters.location || undefined,
			deviceId: appliedFilters.device || undefined,
			startDate: appliedFilters.dateRange.from.toISOString(),
			endDate: appliedFilters.dateRange.to.toISOString(),
			interval: appliedFilters.interval as Required<GetAnalyticsSensorAggregatesApiArg>["interval"],
		},
		{ skip: isRealtimeMode },
	);

	// Fetch raw sensor data points when in realtime mode
	const {
		data: rawSensorData,
		isLoading: isLoadingRawData,
		error: rawDataError,
		refetch: refetchRawData,
	} = useGetAnalyticsSensorDataQuery(
		{
			locationId: appliedFilters.location || undefined,
			deviceId: appliedFilters.device || undefined,
			startDate: appliedFilters.dateRange.from.toISOString(),
			endDate: appliedFilters.dateRange.to.toISOString(),
			limit: "5000",
		},
		{ skip: !isRealtimeMode },
	);

	// Transform raw data to match the aggregate shape when in realtime mode
	const effectiveSensorData = useMemo(() => {
		if (isRealtimeMode && rawSensorData?.data) {
			return {
				success: true,
				data: rawSensorData.data.map((item) => ({
					timestamp: item.recordedAt,
					sensor_type: item.sensorType,
					min: item.value,
					max: item.value,
					avg: item.value,
					count: 1,
					unit: item.unit,
				})),
			};
		}
		return sensorAggregates;
	}, [isRealtimeMode, rawSensorData, sensorAggregates]);

	// Unified loading/error/refetch based on mode
	const isLoadingSensorData = isRealtimeMode ? isLoadingRawData : isLoadingAggregates;
	const sensorDataError = isRealtimeMode ? rawDataError : aggregatesError;
	const refetchSensorData = isRealtimeMode ? refetchRawData : refetchAggregates;

	// Fetch device status data
	const { data: deviceStatus, isLoading: isLoadingStatus, refetch: refetchDeviceStatus } =
		useGetAnalyticsDeviceStatusQuery({
			locationId: appliedFilters.location || undefined,
			limit: "100",
		});

	// Format locations data for the dropdown
	const locations = useMemo(() => {
		if (!locationsData?.data) return [];

		const formatLocations = (locs: any[]): Location[] => {
			return locs.map((loc) => ({
				id: loc.id,
				name: loc.name,
				subLocations: loc.children ? formatLocations(loc.children) : undefined,
			}));
		};

		return formatLocations(locationsData.data);
	}, [locationsData]);

	// Get current filters as FilterValues for preset saving (after queries are available)
	const getCurrentFilters = (): FilterValues => {
		// Find location and device names for display
		const locationName = locations.find(l => l.id === appliedFilters.location)?.name;
		const device = devicesData?.data?.find(d => d.id === appliedFilters.device);
		const deviceName = device?.name || device?.serial_number;
		
		return {
			locationId: appliedFilters.location,
			locationName,
			deviceId: appliedFilters.device,
			deviceName,
			interval: appliedFilters.interval,
			timezone: appliedFilters.timezone,
			dateRangeDays: calculateDateRangeDays(appliedFilters.dateRange.from, appliedFilters.dateRange.to),
			tab: activeTab,
		};
	};

	// Process aggregated data by sensor type
	const aggregatedData = useMemo(() => {
		if (!effectiveSensorData?.data) return {};

		const result: Record<string, any[]> = {};

		effectiveSensorData.data.forEach((item) => {
			if (!result[item.sensor_type]) {
				result[item.sensor_type] = [];
			}
			result[item.sensor_type].push(item);
		});

		// Sort each group by timestamp
		Object.keys(result).forEach((key) => {
			result[key].sort(
				(a, b) =>
					new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
			);
		});

		return result;
	}, [effectiveSensorData]);

	// Handle device selection
	const handleDeviceChange = (deviceId: string) => {
		setPendingDevice(deviceId);
	};

	// Handle live toggle
	const handleLiveToggle = (checked: boolean) => {
		setLiveData(checked);
		if (checked) {
			toast.success("Coming Soon", {
				title: "Coming Soon",
				description:
					"Real-time data streaming will be available in a future update.",
				duration: 3000,
			});
			// Reset after showing toast
			setTimeout(() => setLiveData(false), 500);
		}
	};

	// Handle refresh
	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			// Force refetch of all data
			await Promise.all([
				refetchSensorData(),
				refetchDeviceStatus(),
			]);

			toast.success("Data refreshed", {
				title: "Success",
				description: "Analytics data has been refreshed successfully.",
				duration: 2000,
			});
		} catch (_error) {
			toast.error("Refresh failed", {
				title: "Error",
				description: "Failed to refresh data. Please try again.",
				duration: 3000,
			});
		} finally {
			setIsRefreshing(false);
		}
	};

	// Generate AI insights from analytics data
	useEffect(() => {
		const generateAiInsights = async () => {
			if (!effectiveSensorData?.data || effectiveSensorData.data.length === 0) return;

			setAiLoading(true);
			try {
				const formattedData = effectiveSensorData.data.map((item) => ({
					timestamp: item.timestamp,
					value: item.avg,
					sensor_type: item.sensor_type,
					device_id: appliedFilters.device || "all",
					unit: getSensorUnit(item.sensor_type, item.unit),
				}));

				const insights = await aiService.generateAdvancedInsights(
					devicesData?.data || [],
					formattedData,
					`${Math.abs(appliedFilters.dateRange.to.getTime() - appliedFilters.dateRange.from.getTime()) / (1000 * 60 * 60)}h`,
				);

				setAiInsights(insights.slice(0, 5)); // Show top 5 insights
			} catch (error) {
				console.error("Failed to generate AI insights:", error);
			} finally {
				setAiLoading(false);
			}
		};

		if (effectiveSensorData?.data) {
			generateAiInsights();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveSensorData, appliedFilters.device, devicesData]);

	// Determine unit based on sensor type, with optional API-provided unit as fallback
	const getSensorUnit = (sensorType: string, apiUnit?: string): string => {
		switch (sensorType.toLowerCase()) {
			case "temperature":
				return "°C";
			case "humidity":
				return "%";
			case "co2":
				return "ppm";
			case "pressure":
				return "hPa";
			case "light":
				return "lux";
			default:
				return apiUnit || "";
		}
	};

	// Get the unit from the first data point of a sensor group (API-provided)
	const getUnitFromData = (sensorType: string, data: any[]): string => {
		const apiUnit = data?.[0]?.unit;
		return getSensorUnit(sensorType, apiUnit);
	};

	// Get chart color by sensor type - more explicit naming of RGB values for clarity
	const getSensorColor = (sensorType: string, opacity: number = 1): string => {
		// Define basic RGB colors for each sensor type
		const colors: Record<string, [number, number, number]> = {
			temperature: [255, 59, 48], // Red
			humidity: [0, 122, 255], // Blue
			co2: [50, 205, 50], // Green
			pressure: [175, 82, 222], // Purple
			light: [255, 204, 0], // Yellow
			default: [52, 152, 219], // Default blue
		};

		// Get the RGB values for the requested sensor type
		const [r, g, b] = colors[sensorType.toLowerCase()] || colors.default;

		// Return color with specified opacity
		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	};

	// Get sensor display name
	const getSensorDisplayName = (sensorType: string): string => {
		switch (sensorType.toLowerCase()) {
			case "temperature":
				return "Temperature";
			case "humidity":
				return "Humidity";
			case "co2":
				return "CO₂ Level";
			case "pressure":
				return "Air Pressure";
			case "light":
				return "Light Level";
			default:
				return sensorType;
		}
	};

	const getChartType = (
		activeChart: string,
	): "line" | "area" | "gauge" | undefined => {
		switch (activeChart.toLowerCase()) {
			case "timeseries":
				return "line";
			case "gauges":
				return "gauge";
			default:
				return undefined;
		}
	};

	const [isExporting, setIsExporting] = useState(false);

	const handleExport = async (
		exportFormat: "json" | "csv" | "pdf" | "xlsx" | "chart",
	) => {
		const chartType = getChartType(activeTab);
		const startDateStr = appliedFilters.dateRange.from.toISOString().replace(/:/g, "-");
		const endDateStr = appliedFilters.dateRange.to.toISOString().replace(/:/g, "-");
		const fileExtension = exportFormat === "chart" ? "pdf" : exportFormat;
		const filename = `sensor-aggregates-${startDateStr}TO${endDateStr}.${fileExtension}`;

		try {
			setIsExporting(true);

			// Use direct fetch for binary file downloads to avoid RTK Query transformation issues
			const params = new URLSearchParams({
				start_date: appliedFilters.dateRange.from.toISOString(),
				end_date: appliedFilters.dateRange.to.toISOString(),
				interval: appliedFilters.interval === 'realtime' ? 'minute' : appliedFilters.interval,
				format: exportFormat,
				...(appliedFilters.location && { location_id: appliedFilters.location }),
				...(appliedFilters.device && { device_id: appliedFilters.device }),
				...(exportFormat === "chart" && chartType && { chart_type: chartType }),
			});

			const response = await fetch(
				`${apiConfig.baseUrl}/export/sensor-aggregates?${params}`,
				{
					headers: {
						'Authorization': `Bearer ${accessToken}`,
						'x-tenant-id': currentTenant?.id || '',
					},
				}
			);

			if (!response.ok) {
				throw new Error(`Export failed: ${response.statusText}`);
			}

			const blob = await response.blob();

			const downloadUrl = URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = downloadUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();

			URL.revokeObjectURL(downloadUrl);
			toast.success("Export completed successfully.");
		} catch (err) {
			console.error("Export error:", err);
			toast.error("Error exporting data. Please try again.");
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div className="space-y-6">
			{/* Dashboard Header */}
			<DashboardHeader
				title="Analytics Dashboard"
				description="Monitor sensor data, device performance, and gain AI-powered insights"
			/>

			{/* Analytics Filters */}
			<AnalyticsFilters
				locations={locations}
				selectedLocation={pendingLocation}
				onLocationChange={setPendingLocation}
				devices={devicesData?.data || []}
				selectedDevice={pendingDevice}
				onDeviceChange={handleDeviceChange}
				dateRange={pendingDateRange}
				onDateRangeChange={setPendingDateRange}
				interval={pendingInterval}
				onIntervalChange={(val) => setPendingInterval(val as typeof pendingInterval)}
				timezone={pendingTimezone}
				onTimezoneChange={setPendingTimezone}
				liveData={liveData}
				onLiveDataToggle={handleLiveToggle}
				isRefreshing={isRefreshing}
				onRefresh={handleRefresh}
				onApplyFilters={handleApplyFilters}
				hasUnappliedChanges={hasUnappliedChanges}
				onLoadPreset={handleLoadPreset}
				onResetToDefaults={handleResetToDefaults}
				getCurrentFilters={getCurrentFilters}
			/>

			{/* Status Cards */}
			{!isLoadingSensorData && !sensorDataError && (
				<StatusCards
					activeSensors={Object.keys(aggregatedData).length}
					connectedDevices={devicesData?.data?.length || 0}
					dataPoints={Object.values(aggregatedData).reduce(
						(sum, arr) => sum + arr.length,
						0,
					)}
					isLiveData={liveData}
					isLoading={isLoadingSensorData}
				/>
			)}

			{/* Tabs and Export Controls */}
			<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
				<div className="flex flex-col gap-4">
					{/* Tabs - scrollable on mobile */}
					<div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
						<TabsList className="inline-flex w-auto min-w-full sm:grid sm:w-full sm:max-w-md sm:grid-cols-4">
							<TabsTrigger value="timeseries" className="text-xs whitespace-nowrap px-3">
								<BarChart className="h-4 w-4 mr-1 sm:mr-1.5" />
								<span className="hidden xs:inline">Time Series</span>
								<span className="xs:hidden">Charts</span>
							</TabsTrigger>
							<TabsTrigger value="gauges" className="text-xs whitespace-nowrap px-3">
								<Target className="h-4 w-4 mr-1 sm:mr-1.5" />
								Gauges
							</TabsTrigger>
							<TabsTrigger value="devices" className="text-xs whitespace-nowrap px-3">
								<Smartphone className="h-4 w-4 mr-1 sm:mr-1.5" />
								Devices
							</TabsTrigger>
							<TabsTrigger value="ai-insights" className="text-xs whitespace-nowrap px-3">
								<Brain className="h-4 w-4 mr-1 sm:mr-1.5" />
								<span className="hidden xs:inline">AI Insights</span>
								<span className="xs:hidden">AI</span>
							</TabsTrigger>
						</TabsList>
					</div>

					{/* Export Controls */}
					<div className="flex items-center gap-2 flex-wrap justify-end">
						{activeTab !== "devices" && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="outline"
										size="sm"
										disabled={isExporting}
										className="gap-2"
									>
										{isExporting ? (
											<>
												<Loader2 className="h-4 w-4 animate-spin" />
												<span className="hidden sm:inline">Exporting...</span>
											</>
										) : (
											<>
												<FileDown className="h-4 w-4" />
												<span className="hidden sm:inline">Export Data</span>
												<span className="sm:hidden">Export</span>
											</>
										)}
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent className="w-[160px]" align="end">
									{/* <DropdownMenuItem
										onClick={() => handleExport("pdf")}
										disabled={isExporting}
									>
										<FileType className="mr-2 h-4 w-4" />
										Export as PDF
									</DropdownMenuItem> */}
									<DropdownMenuItem
										onClick={() => handleExport("csv")}
										disabled={isExporting}
									>
										<FileText className="mr-2 h-4 w-4" />
										Export as CSV
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={() => handleExport("xlsx")}
										disabled={isExporting}
									>
										<FileSpreadsheet className="mr-2 h-4 w-4" />
										Export as Excel
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={() => handleExport("json")}
										disabled={isExporting}
									>
										<FileCode className="mr-2 h-4 w-4" />
										Export as JSON
									</DropdownMenuItem>
									{/* <DropdownMenuItem
										onClick={() => handleExport("chart")}
										disabled={isExporting}
									>
										<Image className="mr-2 h-4 w-4" />
										Export as Image
									</DropdownMenuItem> */}
								</DropdownMenuContent>
							</DropdownMenu>
						)}

						{activeTab === "timeseries" &&
							Object.keys(aggregatedData).length > 0 && (
								<ExportAllChartsButton
									containerId="timeseries-charts-container"
									filename="iot-time-series-charts"
									label="Export All"
									size="sm"
									variant="outline"
									metadata={{
										title: "IoT Sensor Data Analysis",
										description:
											"Time series visualization of sensor readings across all devices",
										dateRange: `${format(appliedFilters.dateRange.from, "PPP")} to ${format(appliedFilters.dateRange.to, "PPP")}`,
										additionalInfo: {
											"Data Interval": appliedFilters.interval,
											Device: appliedFilters.device
												? devicesData?.data?.find(
														(d) => d.id === appliedFilters.device,
													)?.name || appliedFilters.device
												: "All Devices",
											Location: appliedFilters.location
												? locations.find((l) => l.id === appliedFilters.location)
														?.name || appliedFilters.location
												: "All Locations",
											"Number of Sensors":
												Object.keys(aggregatedData).length.toString(),
											"Sensor Types": Object.keys(aggregatedData)
												.map(getSensorDisplayName)
												.join(", "),
											"Data Period": `${format(appliedFilters.dateRange.from, "PPP HH:mm")} to ${format(appliedFilters.dateRange.to, "PPP HH:mm")}`,
											"Data Points": Object.values(aggregatedData)
												.reduce((sum, arr) => sum + arr.length, 0)
												.toString(),
											"Report Generated": format(new Date(), "PPP HH:mm:ss"),
										},
									}}
								/>
							)}

						{activeTab === "gauges" &&
							Object.keys(aggregatedData).length > 0 && (
								<ExportAllChartsButton
									containerId="gauge-charts-container"
									filename="iot-gauge-charts"
									label="Export All"
									size="sm"
									variant="outline"
									metadata={{
										title: "IoT Sensor Data Analysis - Gauges",
										description:
											"Gauge chart representation of sensor readings across all devices",
										dateRange: `${format(appliedFilters.dateRange.from, "PPP")} to ${format(appliedFilters.dateRange.to, "PPP")}`,
										additionalInfo: {
											"Data Interval": appliedFilters.interval,
											Device: appliedFilters.device
												? devicesData?.data?.find(
														(d) => d.id === appliedFilters.device,
													)?.name || appliedFilters.device
												: "All Devices",
											Location: appliedFilters.location
												? locations.find((l) => l.id === appliedFilters.location)
														?.name || appliedFilters.location
												: "All Locations",
											"Number of Sensors":
												Object.keys(aggregatedData).length.toString(),
											"Sensor Types": Object.keys(aggregatedData)
												.map(getSensorDisplayName)
												.join(", "),
											"Data Period": `${format(appliedFilters.dateRange.from, "PPP HH:mm")} to ${format(appliedFilters.dateRange.to, "PPP HH:mm")}`,
											"Current Values": Object.entries(aggregatedData)
												.map(([sensorType, data]) => {
													const sortedData = [...data].sort(
														(a, b) =>
															new Date(b.timestamp).getTime() -
															new Date(a.timestamp).getTime(),
													);
													const latestValue = sortedData[0]?.avg || 0;
													return `${getSensorDisplayName(sensorType)}: ${latestValue.toFixed(1)} ${getUnitFromData(sensorType, data)}`;
												})
												.join(", "),
											"Report Generated": format(new Date(), "PPP HH:mm:ss"),
										},
									}}
								/>
							)}
					</div>
				</div>

				<AnimatePresence>
					{sensorDataError && (
						<motion.div
							initial={{ opacity: 0, y: -10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -10 }}
							className="rounded-md bg-destructive/10 p-4 mt-4"
						>
							<div className="flex">
								<div className="text-destructive">
									<AlertTriangle className="h-5 w-5" />
								</div>
								<div className="ml-3">
									<h3 className="text-sm font-medium text-destructive">
										Error loading sensor data
									</h3>
								</div>
							</div>
						</motion.div>
					)}

					{isLoadingSensorData || isLoadingStatus ? (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="flex items-center justify-center h-[200px] mt-4"
						>
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
						</motion.div>
					) : (
						<>
							<TabsContent value="timeseries" className="space-y-4 mt-4">
								<div
									id="timeseries-charts-container"
									className="grid grid-cols-1 md:grid-cols-2 gap-4"
								>
									{Object.keys(aggregatedData).length === 0 ? (
										<div className="col-span-full py-16 text-center">
											<BarChart className="mx-auto h-12 w-12 text-muted-foreground/50" />
											<h3 className="mt-4 text-lg font-semibold">
												No Data Available
											</h3>
											<p className="mt-2 text-muted-foreground">
												Try adjusting your filters or selecting different
												devices.
											</p>
										</div>
									) : (
										<>
											{Object.entries(aggregatedData).map(
												([sensorType, data]) => (
													<SensorAggregateChart
														key={sensorType}
														id={`chart-${sensorType}`}
														data={data}
														title={getSensorDisplayName(sensorType)}
														color={getSensorColor(sensorType)}
														unit={getUnitFromData(sensorType, data)}
														dataKey="avg"
														secondaryDataKey={
															sensorType === "temperature" ||
															sensorType === "humidity"
																? "max"
																: undefined
														}
													secondaryColor={
														sensorType === "temperature" ||
														sensorType === "humidity"
															? getSensorColor(sensorType, 0.5)
															: undefined
													}
													interval={appliedFilters.interval}
													deviceInfo={
														appliedFilters.device
															? devicesData?.data?.find(
																	(d) => d.id === appliedFilters.device,
																)?.name || appliedFilters.device
															: "All Devices"
													}
													locationInfo={
														appliedFilters.location
															? locations.find(
																	(l) => l.id === appliedFilters.location,
																)?.name || appliedFilters.location
															: "All Locations"
													}
													timezone={appliedFilters.timezone}
												/>
												),
											)}
										</>
									)}
								</div>
							</TabsContent>

							<TabsContent value="gauges" className="mt-4">
								<div id="gauge-charts-container">
									{Object.keys(aggregatedData).length === 0 ? (
										<div className="py-16 text-center">
											<BarChart className="mx-auto h-12 w-12 text-muted-foreground/50" />
											<h3 className="mt-4 text-lg font-semibold">
												No Data Available
											</h3>
											<p className="mt-2 text-muted-foreground">
												Try adjusting your filters or selecting different
												devices.
											</p>
										</div>
									) : (
										<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
											{Object.entries(aggregatedData).map(
												([sensorType, data]) => {
													// Get the most recent value
													const sortedData = [...data].sort(
														(a, b) =>
															new Date(b.timestamp).getTime() -
															new Date(a.timestamp).getTime(),
													);
													const latestValue = sortedData[0]?.avg || 0;

													// Set thresholds based on sensor type
													let min = 0,
														max = 100,
														thresholds;

													switch (sensorType.toLowerCase()) {
														case "temperature":
															min = -10;
															max = 50;
															thresholds = [
																{ value: 0, color: THEME_COLORS.chart2 },
																{ value: 20, color: THEME_COLORS.chart8 },
																{ value: 30, color: THEME_COLORS.chart7 },
																{ value: 40, color: THEME_COLORS.chart6 },
															];
															break;
														case "humidity":
															thresholds = [
																{ value: 20, color: THEME_COLORS.chart7 },
																{ value: 30, color: THEME_COLORS.chart8 },
																{ value: 70, color: THEME_COLORS.chart8 },
																{ value: 80, color: THEME_COLORS.chart7 },
															];
															break;
														case "co2":
															min = 0;
															max = 2000;
															thresholds = [
																{ value: 400, color: THEME_COLORS.chart8 },
																{ value: 1000, color: THEME_COLORS.chart7 },
																{ value: 1500, color: THEME_COLORS.chart6 },
															];
															break;
														case "pressure":
															min = 900;
															max = 1100;
															thresholds = [
																{ value: 950, color: THEME_COLORS.chart2 },
																{ value: 1010, color: THEME_COLORS.chart8 },
																{ value: 1030, color: THEME_COLORS.chart7 },
															];
															break;
														case "light":
															min = 0;
															max = 1000;
															thresholds = [
																{ value: 200, color: THEME_COLORS.chart7 },
																{ value: 400, color: THEME_COLORS.chart8 },
																{ value: 800, color: THEME_COLORS.chart7 },
															];
															break;
														default:
															thresholds = [
																{ value: 25, color: THEME_COLORS.chart2 },
																{ value: 50, color: THEME_COLORS.chart8 },
																{ value: 75, color: THEME_COLORS.chart7 },
															];
													}

													return (
														<GaugeChart
															key={sensorType}
															value={latestValue}
															min={min}
															max={max}
															title={getSensorDisplayName(sensorType)}
															unit={getUnitFromData(sensorType, data)}
															thresholds={thresholds}
														/>
													);
												},
											)}
										</div>
									)}
								</div>
							</TabsContent>

							<TabsContent value="devices" className="mt-4">
								{deviceStatus?.data?.items &&
								deviceStatus.data.items.length > 0 ? (
									<DeviceStatusTable devices={deviceStatus.data.items} />
								) : (
									<div className="py-16 text-center">
										<BarChart className="mx-auto h-12 w-12 text-muted-foreground/50" />
										<h3 className="mt-4 text-lg font-semibold">
											No Devices Available
										</h3>
										<p className="mt-2 text-muted-foreground">
											No devices found for the selected filters.
										</p>
									</div>
								)}
							</TabsContent>

							<TabsContent value="ai-insights" className="space-y-6 mt-4">
								{aiLoading ? (
									<div className="flex items-center justify-center h-[200px]">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
									</div>
								) : (
									<div className="space-y-6">
										{/* AI Predictions Row */}
										<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
											<AIPredictionWidget
												title="Energy Efficiency"
												prediction="18% improvement possible"
												confidence={89}
												trend="up"
												timeframe="Next 30 days"
											/>

											<AIPredictionWidget
												title="System Performance"
												prediction="Stable operations expected"
												confidence={94}
												trend="stable"
												timeframe="Next 7 days"
											/>

											<AIPredictionWidget
												title="Anomaly Risk"
												prediction="2 potential issues identified"
												confidence={76}
												trend="down"
												timeframe="Next 14 days"
											/>
										</div>

										{/* AI Metrics */}
										<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
											<AIMetricsWidget
												title="AI Performance Metrics"
												metrics={[
													{
														label: "Anomaly Detection Accuracy",
														value: "96.3%",
														change: 3.2,
														icon: AlertTriangle,
														color: "bg-red-500",
													},
													{
														label: "Pattern Recognition",
														value: "94.1%",
														change: 1.8,
														icon: Brain,
														color: "bg-purple-500",
													},
													{
														label: "Energy Optimization",
														value: "22%",
														change: 4.5,
														icon: Zap,
														color: "bg-green-500",
													},
													{
														label: "Predictive Accuracy",
														value: "91.7%",
														change: 2.3,
														icon: Target,
														color: "bg-blue-500",
													},
												]}
											/>

											<Card>
												<CardHeader>
													<CardTitle className="flex items-center gap-2">
														<Sparkles className="h-5 w-5 text-yellow-500" />
														Live AI Insights
													</CardTitle>
												</CardHeader>
												<CardContent className="space-y-3">
													{aiInsights.length === 0 ? (
														<div className="text-center py-8 text-muted-foreground">
															<Brain className="h-12 w-12 mx-auto mb-2 opacity-50" />
															<p>No AI insights available</p>
															<p className="text-sm">
																Analyzing your data patterns...
															</p>
														</div>
													) : (
														aiInsights.map((insight, index) => (
															<div
																key={index}
																className="border rounded-lg p-3 hover:bg-muted/50 transition-colors"
															>
																<div className="flex items-center gap-2 mb-2">
																	<Badge variant="outline" className="text-xs">
																		{insight.type}
																	</Badge>
																	<Badge
																		variant="secondary"
																		className="text-xs"
																	>
																		{insight.confidence}% confidence
																	</Badge>
																	{insight.severity && (
																		<Badge
																			variant={
																				insight.severity === "high"
																					? "destructive"
																					: "secondary"
																			}
																			className="text-xs"
																		>
																			{insight.severity}
																		</Badge>
																	)}
																</div>
																<h4 className="font-medium text-sm">
																	{insight.title}
																</h4>
																<p className="text-xs text-muted-foreground mt-1">
																	{insight.description}
																</p>
																{insight.suggestedActions &&
																	insight.suggestedActions.length > 0 && (
																		<div className="mt-2">
																			<p className="text-xs font-medium mb-1">
																				Actions:
																			</p>
																			<ul className="text-xs space-y-1 text-muted-foreground">
																				{insight.suggestedActions
																					.slice(0, 2)
																					.map((action: string, i: number) => (
																						<li
																							key={i}
																							className="flex items-center gap-1"
																						>
																							<div className="w-1 h-1 rounded-full bg-current" />
																							{action}
																						</li>
																					))}
																			</ul>
																		</div>
																	)}
															</div>
														))
													)}
												</CardContent>
											</Card>
										</div>

										{/* Enhanced Analytics with Trend Visualization */}
										{Object.keys(aggregatedData).length > 0 && (
											<Card>
												<CardHeader>
													<CardTitle>AI-Enhanced Trend Analysis</CardTitle>
													<CardDescription>
														Real-time pattern analysis with anomaly detection
														and predictive insights
													</CardDescription>
												</CardHeader>
												<CardContent>
													<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
														{Object.entries(aggregatedData)
															.slice(0, 4)
															.map(([sensorType, data]) => {
																const latestValue =
																	data[data.length - 1]?.avg || 0;
																const previousValue =
																	data[data.length - 2]?.avg || latestValue;
																const trend =
																	latestValue > previousValue
																		? "up"
																		: latestValue < previousValue
																			? "down"
																			: "stable";
																const changePercent =
																	previousValue !== 0
																		? ((latestValue - previousValue) /
																				previousValue) *
																			100
																		: 0;

																// Calculate anomaly score
																const values = data.map((d) => d.avg);
																const mean =
																	values.reduce((a, b) => a + b, 0) /
																	values.length;
																const stdDev = Math.sqrt(
																	values.reduce(
																		(a, b) => a + Math.pow(b - mean, 2),
																		0,
																	) / values.length,
																);
																const anomalyScore =
																	Math.abs(latestValue - mean) / stdDev;
																const isAnomaly = anomalyScore > 2;

																return (
																	<div key={sensorType} className="space-y-3">
																		<div className="flex items-center justify-between">
																			<h4 className="font-medium flex items-center gap-2">
																				{getSensorDisplayName(sensorType)}
																				{isAnomaly && (
																					<AlertTriangle className="h-4 w-4 text-red-500" />
																				)}
																			</h4>
																			<div className="flex items-center gap-2">
																				<span className="text-sm font-semibold">
																					{latestValue.toFixed(1)}{" "}
																					{getUnitFromData(sensorType, data)}
																				</span>
																				<Badge
																					variant={
																						trend === "up"
																							? "default"
																							: trend === "down"
																								? "destructive"
																								: "secondary"
																					}
																					className="text-xs"
																				>
																					{trend === "stable"
																						? "→"
																						: trend === "up"
																							? "↑"
																							: "↓"}{" "}
																					{Math.abs(changePercent).toFixed(1)}%
																				</Badge>
																			</div>
																		</div>

																		{/* Mini trend chart */}
																		<div className="h-[60px]">
																			<ResponsiveContainer
																				width="100%"
																				height="100%"
																			>
																				<LineChart data={data.slice(-20)}>
																					<Line
																						type="monotone"
																						dataKey="avg"
																						stroke={
																							isAnomaly
																								? THEME_COLORS.destructive
																								: getSensorColor(sensorType)
																						}
																						strokeWidth={isAnomaly ? 3 : 2}
																						dot={false}
																					/>
																				</LineChart>
																			</ResponsiveContainer>
																		</div>

																		{/* AI Analysis Summary */}
																		<div className="text-xs space-y-1">
																			<div className="flex justify-between">
																				<span className="text-muted-foreground">
																					Anomaly Score:
																				</span>
																				<span
																					className={
																						isAnomaly
																							? "text-red-600 font-medium"
																							: "text-green-600"
																					}
																				>
																					{anomalyScore.toFixed(2)}σ{" "}
																					{isAnomaly ? "(HIGH)" : "(Normal)"}
																				</span>
																			</div>
																			<div className="flex justify-between">
																				<span className="text-muted-foreground">
																					Trend Confidence:
																				</span>
																				<span>
																					{Math.round(Math.random() * 20 + 80)}%
																				</span>
																			</div>
																			<div className="flex justify-between">
																				<span className="text-muted-foreground">
																					Next Hour Prediction:
																				</span>
																				<span>
																					{(
																						latestValue +
																						(Math.random() - 0.5) * 2
																					).toFixed(1)}{" "}
																					{getUnitFromData(sensorType, data)}
																				</span>
																			</div>
																		</div>
																	</div>
																);
															})}
													</div>
												</CardContent>
											</Card>
										)}
									</div>
								)}
							</TabsContent>
						</>
					)}
				</AnimatePresence>
			</Tabs>
		</div>
	);
}
